// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Filter, Users, DollarSign, Calendar, Target } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import WhiteLabelPipeline from '@/components/sales/WhiteLabelPipeline';
import WhiteLabelProspectForm from '@/components/sales/WhiteLabelProspectForm';
import WhiteLabelDemoManager from '@/components/sales/WhiteLabelDemoManager';
import WhiteLabelImplementationTracker from '@/components/sales/WhiteLabelImplementationTracker';

const WhiteLabelPage: React.FC = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('pipeline');
  const [searchTerm, setSearchTerm] = useState('');
  const [showProspectForm, setShowProspectForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeImplementations: 0,
    pendingDemos: 0,
    monthlyRevenue: 0,
    totalProspects: 0,
    implementationsTrend: 0,
    demosTrend: 0,
    revenueTrend: 0,
    prospectsTrend: 0
  });

  useEffect(() => {
    if (profile?.id) {
      loadStats();
    }
  }, [profile?.id]);

  const loadStats = async () => {
    try {
      setLoading(true);

      // Get active implementations count
      const { data: implementations, error: implError } = await supabase
        .from('white_label_implementations')
        .select('id, monthly_fee')
        .or('status.eq.development,status.eq.testing,status.eq.deployed');

      if (implError) throw implError;

      // Get pending demos count
      const { data: demos, error: demosError } = await supabase
        .from('white_label_demos')
        .select('id')
        .eq('status', 'scheduled');

      if (demosError) throw demosError;

      // Get total prospects count
      const { data: prospects, error: prospectsError } = await supabase
        .from('white_label_prospects')
        .select('id')
        .neq('pipeline_stage', 'closed_lost');

      if (prospectsError) throw prospectsError;

      // Calculate monthly revenue from active implementations
      const monthlyRevenue = implementations?.reduce((sum, impl) => 
        sum + (impl.monthly_fee || 0), 0) || 0;

      // Get trends (compare with last month - simplified for now)
      const thisWeekStart = new Date();
      thisWeekStart.setDate(thisWeekStart.getDate() - 7);

      const { data: newProspects } = await supabase
        .from('white_label_prospects')
        .select('id')
        .gte('created_at', thisWeekStart.toISOString());

      const { data: newDemos } = await supabase
        .from('white_label_demos')
        .select('id')
        .eq('status', 'scheduled')
        .gte('created_at', thisWeekStart.toISOString());

      setStats({
        activeImplementations: implementations?.length || 0,
        pendingDemos: demos?.length || 0,
        monthlyRevenue: monthlyRevenue,
        totalProspects: prospects?.length || 0,
        implementationsTrend: 0, // Could be calculated by comparing with previous month
        demosTrend: newDemos?.length || 0,
        revenueTrend: 0, // Could be calculated by comparing revenue change
        prospectsTrend: newProspects?.length || 0
      });

    } catch (error) {
      console.error('Error loading white label stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const quickStats = [
    {
      title: 'Active Implementations',
      value: loading ? '...' : stats.activeImplementations.toString(),
      description: 'Currently in progress',
      icon: Target,
      trend: stats.implementationsTrend > 0 ? `+${stats.implementationsTrend} this month` : 'No change'
    },
    {
      title: 'Pending Demos',
      value: loading ? '...' : stats.pendingDemos.toString(),
      description: 'Scheduled this week',
      icon: Calendar,
      trend: stats.demosTrend > 0 ? `+${stats.demosTrend} scheduled` : 'No new demos'
    },
    {
      title: 'Monthly Revenue',
      value: loading ? '...' : `$${stats.monthlyRevenue.toLocaleString()}`,
      description: 'From active clients',
      icon: DollarSign,
      trend: stats.revenueTrend > 0 ? `+${stats.revenueTrend}% vs last month` : 'No change'
    },
    {
      title: 'Total Prospects',
      value: loading ? '...' : stats.totalProspects.toString(),
      description: 'In pipeline',
      icon: Users,
      trend: stats.prospectsTrend > 0 ? `+${stats.prospectsTrend} new this week` : 'No new prospects'
    }
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">White Label Management</h1>
          <p className="text-muted-foreground">
            Manage event organizer prospects and implementation pipeline
          </p>
        </div>
        <Button onClick={() => setShowProspectForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Prospect
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
              <Badge variant="secondary" className="text-xs mt-1">
                {stat.trend}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search prospects, organizations, or contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Filters
        </Button>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="demos">Demo Manager</TabsTrigger>
          <TabsTrigger value="implementations">Implementations</TabsTrigger>
          <TabsTrigger value="proposals">Proposals</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="space-y-4">
          <WhiteLabelPipeline searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="demos" className="space-y-4">
          <WhiteLabelDemoManager searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="implementations" className="space-y-4">
          <WhiteLabelImplementationTracker searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="proposals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Proposal Management</CardTitle>
              <CardDescription>
                Create and track custom proposals for white label implementations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <p className="text-muted-foreground">Proposal management coming soon...</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Prospect Form Modal */}
      {showProspectForm && (
        <WhiteLabelProspectForm
          open={showProspectForm}
          onClose={() => setShowProspectForm(false)}
          onSuccess={() => {
            setShowProspectForm(false);
            loadStats(); // Refresh data
          }}
        />
      )}
    </div>
  );
};

export default WhiteLabelPage;